import typeshedData from '../../../../micropython/main/typeshed.json';
import { FrameworkActionLists, FrameworkClasses, UtilityActions } from '../../../../framework/frameworkClasses';
import { isSupportedMicroPythonStdlibModule } from '../../../../utils/microPythonCompatibility';
import { ImportCandidate, ImportCandidateSource, WorkspaceModuleLike } from './types';

interface ImportCatalog {
  builtins: Set<string>;
  candidatesBySymbol: Map<string, ImportCandidate[]>;
  moduleSources: Map<string, ImportCandidateSource>;
}

interface TypeshedFileMap {
  files: Record<string, string>;
}

const defaultTypeshedFiles = (typeshedData as TypeshedFileMap).files;
const spheroRootModule = 'sphero';
const studentLibraryExtraSymbols = ['SensorType'];

const topLevelDeclarationPattern = /^(?:async\s+def|def|class)\s+([A-Za-z_]\w*)\b|^([A-Za-z_]\w*)\s*(?::[^=\n]+)?=/gm;
const topLevelImportPattern = /^from\s+([.\w]+)\s+import\s+(.+)$/gm;
const relativeModuleSpecPattern = /^(\.+)([\w.]*)$/;
let catalogCache: ImportCatalog | undefined;

function addCandidate(map: Map<string, ImportCandidate[]>, candidate: ImportCandidate) {
  const candidates = map.get(candidate.symbol) ?? [];
  const duplicate = candidates.find(
    (existing) =>
      existing.importType === candidate.importType &&
      existing.module === candidate.module &&
      existing.importedName === candidate.importedName &&
      existing.alias === candidate.alias
  );

  if (duplicate) {
    duplicate.isNamespaceImport ||= candidate.isNamespaceImport;
    return;
  }

  candidates.push(candidate);
  map.set(candidate.symbol, candidates);
}

function getSourceForModule(moduleName: string): ImportCandidateSource {
  return moduleName === 'sphero' || moduleName.startsWith('sphero.') ? 'student-lib' : 'stdlib';
}

function isSupportedCatalogModule(moduleName: string, source: ImportCandidateSource): boolean {
  return source !== 'stdlib' || isSupportedMicroPythonStdlibModule(moduleName);
}

function getSourcePriority(source: ImportCandidateSource): number {
  switch (source) {
    case 'student-lib':
      return 0;
    case 'stdlib':
      return 1;
    case 'workspace':
      return 2;
    case 'sample':
    default:
      return 3;
  }
}

function compareImportCandidates(left: ImportCandidate, right: ImportCandidate): number {
  const sourceRank = getSourcePriority(left.source) - getSourcePriority(right.source);
  if (sourceRank !== 0) {
    return sourceRank;
  }

  const priorityDelta = right.priority - left.priority;
  if (priorityDelta !== 0) {
    return priorityDelta;
  }

  const namespaceDelta = Number(Boolean(right.isNamespaceImport)) - Number(Boolean(left.isNamespaceImport));
  if (namespaceDelta !== 0) {
    return namespaceDelta;
  }

  const moduleDelta = left.module.localeCompare(right.module);
  if (moduleDelta !== 0) {
    return moduleDelta;
  }

  return (left.importedName ?? left.symbol).localeCompare(right.importedName ?? right.symbol);
}

function getModuleNameFromTypeshedPath(path: string): string | null {
  const match = path.match(/^\/typeshed\/stdlib\/(.+)\.pyi$/);
  if (!match) {
    return null;
  }

  const modulePath = match[1];
  if (modulePath.endsWith('/__init__')) {
    return modulePath.slice(0, -'/__init__'.length).replace(/\//g, '.');
  }

  return modulePath.replace(/\//g, '.');
}

function isLikelyFrameworkTypeName(typeName: string): boolean {
  return /^[A-Z][A-Za-z0-9_]*$/.test(typeName);
}

function collectFrameworkRootExports(): Set<string> {
  const rootExports = new Set<string>(studentLibraryExtraSymbols);

  for (const frameworkClass of [...FrameworkClasses, UtilityActions]) {
    rootExports.add(frameworkClass.name);

    for (const initialization of frameworkClass.initialization ?? []) {
      if (typeof initialization.type === 'string' && isLikelyFrameworkTypeName(initialization.type)) {
        rootExports.add(initialization.type);
      }

      if (typeof initialization.value === 'string' && initialization.value.includes('.')) {
        const [enumType] = initialization.value.split('.');
        if (isLikelyFrameworkTypeName(enumType)) {
          rootExports.add(enumType);
        }
      }
    }

    for (const action of frameworkClass.actions ?? []) {
      for (const argument of action.arguments ?? []) {
        if (typeof argument.type === 'string' && isLikelyFrameworkTypeName(argument.type)) {
          rootExports.add(argument.type);
        }

        if (typeof argument.value === 'string' && argument.value.includes('.')) {
          const [enumType] = argument.value.split('.');
          if (isLikelyFrameworkTypeName(enumType)) {
            rootExports.add(enumType);
          }
        }
      }
    }
  }

  for (const actionList of FrameworkActionLists) {
    for (const action of actionList.actions ?? []) {
      if (isLikelyFrameworkTypeName(action.name)) {
        rootExports.add(action.name);
      }

      for (const argument of action.arguments ?? []) {
        if (typeof argument.type === 'string' && isLikelyFrameworkTypeName(argument.type)) {
          rootExports.add(argument.type);
        }

        if (typeof argument.value === 'string' && argument.value.includes('.')) {
          const [enumType] = argument.value.split('.');
          if (isLikelyFrameworkTypeName(enumType)) {
            rootExports.add(enumType);
          }
        }
      }
    }
  }

  return rootExports;
}

function extractTopLevelExports(content: string): Set<string> {
  const exports = new Set<string>();

  for (const match of content.matchAll(topLevelDeclarationPattern)) {
    const symbol = match[1] ?? match[2];
    if (!symbol || symbol.startsWith('_')) {
      continue;
    }
    exports.add(symbol);
  }

  return exports;
}

function splitImportedItems(rawImportedItems: string): Array<{ name: string; alias?: string }> {
  const normalized = rawImportedItems.replace(/[()]/g, '');

  return normalized
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => {
      if (item === '*') {
        return { name: '*' };
      }

      const match = item.match(/^([A-Za-z_][\w]*)(?:\s+as\s+([A-Za-z_]\w*))?$/);
      if (!match) {
        return { name: item };
      }

      return {
        name: match[1],
        alias: match[2],
      };
    });
}

function resolveRelativeModule(currentModule: string, dots: string, remainder: string): string {
  const packageParts = currentModule.split('.');
  const levelsUp = Math.max(dots.length - 1, 0);
  const baseParts = packageParts.slice(0, Math.max(packageParts.length - levelsUp, 0));

  if (!remainder) {
    return baseParts.join('.');
  }

  return [...baseParts, remainder].filter(Boolean).join('.');
}

function extractReExports(currentModule: string, content: string): Set<string> {
  const exports = new Set<string>();

  for (const match of content.matchAll(topLevelImportPattern)) {
    const moduleSpec = match[1];
    const importedItems = splitImportedItems(match[2]);

    if (!moduleSpec.startsWith('.')) {
      continue;
    }

    const relativeMatch = moduleSpec.match(relativeModuleSpecPattern);
    if (!relativeMatch) {
      continue;
    }

    const resolvedModule = resolveRelativeModule(currentModule, relativeMatch[1], relativeMatch[2]);
    if (!resolvedModule && moduleSpec !== '.') {
      continue;
    }

    for (const importedItem of importedItems) {
      if (importedItem.name === '*') {
        continue;
      }

      const exportedName = importedItem.alias ?? importedItem.name;
      if (exportedName.startsWith('_')) {
        continue;
      }

      exports.add(exportedName);
    }
  }

  return exports;
}

function buildCatalog(typeshedFiles: TypeshedFileMap['files'] = defaultTypeshedFiles): ImportCatalog {
  const builtins = extractTopLevelExports(typeshedFiles['/typeshed/stdlib/builtins.pyi'] ?? '');
  const candidatesBySymbol = new Map<string, ImportCandidate[]>();
  const moduleSources = new Map<string, ImportCandidateSource>();

  moduleSources.set(spheroRootModule, 'student-lib');

  for (const exportedSymbol of collectFrameworkRootExports()) {
    addCandidate(candidatesBySymbol, {
      symbol: exportedSymbol,
      importType: 'from',
      module: spheroRootModule,
      importedName: exportedSymbol,
      priority: 130,
      source: 'student-lib',
    });
  }

  for (const [filePath, content] of Object.entries(typeshedFiles)) {
    const moduleName = getModuleNameFromTypeshedPath(filePath);
    if (!moduleName) {
      continue;
    }

    const source = getSourceForModule(moduleName);
    if (!isSupportedCatalogModule(moduleName, source)) {
      continue;
    }

    moduleSources.set(moduleName, source);

    if (!moduleName.includes('.')) {
      addCandidate(candidatesBySymbol, {
        symbol: moduleName,
        importType: 'import',
        module: moduleName,
        priority: 100,
        source,
        isNamespaceImport: true,
      });
    }

    for (const exportedSymbol of extractTopLevelExports(content)) {
      addCandidate(candidatesBySymbol, {
        symbol: exportedSymbol,
        importType: 'from',
        module: moduleName,
        importedName: exportedSymbol,
        priority: 90,
        source,
      });
    }

    if (filePath.endsWith('/__init__.pyi')) {
      for (const reExportedSymbol of extractReExports(moduleName, content)) {
        addCandidate(candidatesBySymbol, {
          symbol: reExportedSymbol,
          importType: 'from',
          module: moduleName,
          importedName: reExportedSymbol,
          priority: 110,
          source,
          isNamespaceImport: true,
        });
      }
    }
  }

  for (const candidates of candidatesBySymbol.values()) {
    candidates.sort(compareImportCandidates);
  }

  return {
    builtins,
    candidatesBySymbol,
    moduleSources,
  };
}

export function getImportCatalog(): ImportCatalog {
  if (!catalogCache) {
    catalogCache = buildCatalog();
  }

  return catalogCache;
}

export function setImportCatalogTypeshedForTest(typeshedFiles: TypeshedFileMap['files']): () => void {
  const previousCatalog = catalogCache;
  catalogCache = buildCatalog(typeshedFiles);

  return () => {
    catalogCache = previousCatalog;
  };
}

function addWorkspaceCandidate(map: Map<string, ImportCandidate[]>, moduleName: string, symbol: string, isNamespaceImport = false) {
  addCandidate(map, {
    symbol,
    importType: isNamespaceImport ? 'import' : 'from',
    module: moduleName,
    importedName: isNamespaceImport ? undefined : symbol,
    priority: isNamespaceImport ? 70 : 80,
    source: 'workspace',
    isNamespaceImport,
  });
}

export function buildWorkspaceCandidateMap(workspaceModules: WorkspaceModuleLike[]): Map<string, ImportCandidate[]> {
  const candidatesBySymbol = new Map<string, ImportCandidate[]>();

  for (const workspaceModule of workspaceModules) {
    if (!workspaceModule.module) {
      continue;
    }

    addWorkspaceCandidate(candidatesBySymbol, workspaceModule.module, workspaceModule.module, true);

    for (const exportedSymbol of extractTopLevelExports(workspaceModule.code)) {
      addWorkspaceCandidate(candidatesBySymbol, workspaceModule.module, exportedSymbol);
    }
  }

  for (const candidates of candidatesBySymbol.values()) {
    candidates.sort(compareImportCandidates);
  }

  return candidatesBySymbol;
}

export function getModuleExports(moduleName: string, workspaceModules: WorkspaceModuleLike[] = []): Set<string> {
  const catalog = getImportCatalog();
  const exports = new Set<string>();

  for (const [symbol, candidates] of catalog.candidatesBySymbol) {
    if (candidates.some((candidate) => candidate.module === moduleName && candidate.importType === 'from')) {
      exports.add(symbol);
    }
  }

  const workspaceCandidates = buildWorkspaceCandidateMap(workspaceModules);
  for (const [symbol, candidates] of workspaceCandidates) {
    if (candidates.some((candidate) => candidate.module === moduleName && candidate.importType === 'from')) {
      exports.add(symbol);
    }
  }

  return exports;
}

export function hasKnownModule(moduleName: string, workspaceModules: WorkspaceModuleLike[] = []): boolean {
  const workspaceModuleNames = new Set(workspaceModules.map((workspaceModule) => workspaceModule.module));
  return workspaceModuleNames.has(moduleName) || getImportCatalog().moduleSources.has(moduleName);
}

export function getModuleSource(moduleName: string, workspaceModules: WorkspaceModuleLike[] = []): ImportCandidateSource {
  const workspaceModuleNames = new Set(workspaceModules.map((workspaceModule) => workspaceModule.module));
  if (workspaceModuleNames.has(moduleName)) {
    return 'workspace';
  }

  return getImportCatalog().moduleSources.get(moduleName) ?? 'workspace';
}

export { compareImportCandidates };
